import { gql, type QueryHookOptions, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import type { Basin, Query, QueryBasinsWithFilterArgs } from '@/types/GraphqlSchema';
import type { ValuationsFilters as ValuationsFiltersType } from '../../store/valuationsStore';
import { AutoCompleteSimpleComposable } from './AutoCompleteSimpleComposable';
import { FilterKey } from './filterConfig';

type BasinsProps = {
  filters: ValuationsFiltersType;
  onChange: (basins: Basin[]) => void;
  context: string;
};

type BasinFilterVariables = Pick<
  QueryBasinsWithFilterArgs,
  | 'countries'
  | 'assets'
  | 'companies'
  | 'nonOperator'
  | 'operator'
  | 'hasMaTransactions'
  | 'isMAndAOpportunity'
>;

const prepareBasinFilterVariables = (filters: ValuationsFiltersType): BasinFilterVariables => {
  return {
    assets: filters[FilterKey.Assets] || [],
    countries: filters[FilterKey.Countries]?.map(country => country.isoCode) || [],
    companies: filters[FilterKey.Companies]?.map(company => company.name) || [],
    nonOperator: filters[FilterKey.NonOperator],
    operator: filters[FilterKey.Operator],
    hasMaTransactions: false,
    isMAndAOpportunity: filters[FilterKey.Opportunities],
  };
};

const BASINS_WITH_FILTER_QUERY = gql`
  query basinsWithFilter(
    $search: String
    $countries: [String!]
    $assets: [JSON!]
    $companies: [String!]
    $nonOperator: Boolean
    $operator: Boolean
    $hasMaTransactions: Boolean
    $isMAndAOpportunity: Boolean
  ) {
    basinsWithFilter(
      search: $search
      countries: $countries
      assets: $assets
      companies: $companies
      nonOperator: $nonOperator
      operator: $operator
      hasMaTransactions: $hasMaTransactions
      isMAndAOpportunity: $isMAndAOpportunity
    ) {
      totalCount
      basins {
        id
        name
      }
    }
  }
`;

const name = 'Basins';

const note =
  '*Only the top 100 basins will be shown, you can access more basins by typing in the name.';

type BasinsQueryData = Pick<Query, 'basinsWithFilter'>;

export const BasinsFilter = ({ filters, onChange }: BasinsProps) => {
  const [searchValue, setSearchValue] = useState('');
  const filterVariables = prepareBasinFilterVariables(filters);

  const queryVariables: QueryBasinsWithFilterArgs = {
    search: searchValue,
    ...filterVariables,
  };

  const queryOptions: QueryHookOptions<BasinsQueryData, QueryBasinsWithFilterArgs> = {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
  };

  const { data, loading } = useQuery<BasinsQueryData, QueryBasinsWithFilterArgs>(
    BASINS_WITH_FILTER_QUERY,
    queryOptions,
  );

  const basins = Array.isArray(data?.basinsWithFilter?.basins) ? data.basinsWithFilter.basins : [];

  const basinsValue = useMemo(() => {
    const filterBasins = filters[FilterKey.Basins];
    if (Array.isArray(filterBasins)) {
      return filterBasins;
    }
    if (filterBasins && typeof filterBasins === 'object') {
      return [filterBasins];
    }
    return [];
  }, [filters]);

  return (
    <AutoCompleteSimpleComposable<Basin>
      options={basins}
      loading={loading}
      value={basinsValue}
      multiple={true}
      size='medium'
      getOptionLabel={option => (typeof option === 'string' ? option : option.name || '')}
      onChange={(_, newValue) => {
        onChange((newValue as Basin[]) || []);
      }}
      onInputChange={(_, newInputValue) => {
        setSearchValue(newInputValue);
      }}
      renderInput={params => (
        <AutoCompleteSimpleComposable.StyledInput
          params={params}
          label={name}
          data-id='basins-filter-input'
        />
      )}
      renderTags={(value, getTagProps) => {
        const numTags = value.length;
        return (
          <>
            {value.slice(0, 1).map((option, index) => (
              <AutoCompleteSimpleComposable.StyledTag
                key={option.id || index}
                option={option}
                index={index}
                getTagProps={getTagProps}
                getOptionLabel={opt => opt.name || ''}
              />
            ))}
            {numTags > 1 && ` +${numTags - 1}`}
          </>
        );
      }}
      renderOption={(props, option, state) => {
        const isLastOption = basins.indexOf(option) === basins.length - 1;

        return (
          <React.Fragment key={option.id}>
            <AutoCompleteSimpleComposable.Option
              {...props}
              option={option}
              state={state}
              getOptionLabel={opt => opt.name || ''}
            />
            {isLastOption && note && (
              <AutoCompleteSimpleComposable.Note key='note' note={note} isHidden={false} />
            )}
          </React.Fragment>
        );
      }}
      data-id='basins-filter'
    />
  );
};
